/**
 * Сервис для кэширования API запросов
 */
class ApiCache {
    constructor() {
        this.cache = new Map();
        this.pendingRequests = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 минут
    }

    /**
     * Получить данные из кэша или выполнить запрос
     * @param {string} key - ключ кэша
     * @param {Function} requestFn - функция для выполнения запроса
     * @param {number} ttl - время жизни кэша в миллисекундах
     * @returns {Promise}
     */
    async get(key, requestFn, ttl = this.cacheTimeout) {
        const now = Date.now();
        const cached = this.cache.get(key);

        // Проверяем кэш
        if (cached && (now - cached.timestamp) < ttl) {
            return cached.data;
        }

        // Проверяем, есть ли уже выполняющийся запрос
        if (this.pendingRequests.has(key)) {
            return this.pendingRequests.get(key);
        }

        // Выполняем запрос
        const requestPromise = requestFn().then(data => {
            // Сохраняем в кэш
            this.cache.set(key, {
                data,
                timestamp: now
            });

            // Удаляем из pending
            this.pendingRequests.delete(key);

            return data;
        }).catch(error => {
            // Удаляем из pending при ошибке
            this.pendingRequests.delete(key);
            throw error;
        });

        this.pendingRequests.set(key, requestPromise);
        return requestPromise;
    }

    /**
     * Инвалидировать кэш по ключу
     * @param {string} key - ключ кэша
     */
    invalidate(key) {
        this.cache.delete(key);
        this.pendingRequests.delete(key);
    }

    /**
     * Инвалидировать кэш по паттерну
     * @param {string} pattern - регулярное выражение для поиска ключей
     */
    invalidatePattern(pattern) {
        const regex = new RegExp(pattern);
        for (const key of this.cache.keys()) {
            if (regex.test(key)) {
                this.cache.delete(key);
            }
        }
        for (const key of this.pendingRequests.keys()) {
            if (regex.test(key)) {
                this.pendingRequests.delete(key);
            }
        }
    }

    /**
     * Очистить весь кэш
     */
    clear() {
        this.cache.clear();
        this.pendingRequests.clear();
    }

    /**
     * Получить статистику кэша
     */
    getStats() {
        return {
            cacheSize: this.cache.size,
            pendingRequests: this.pendingRequests.size,
            cacheKeys: Array.from(this.cache.keys()),
            pendingKeys: Array.from(this.pendingRequests.keys())
        };
    }
}

// Создаем единственный экземпляр
const apiCache = new ApiCache();

export default apiCache;
